import { getRedirectUrlFromSearch } from "@/lib/utils/auth-redirect";
import { IconType, SiGoogle } from "@icons-pack/react-simple-icons";
import { Button } from "@/components/catalyst/button";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { signIn } from "@/lib/auth/client";
import { useState, Suspense } from "react";

type SocialProvider = "google" | "zoom";

type SocialSignInButtonProps = {
  providerLabel: ProviderLabel;
  disabled?: boolean;
};

type ProviderLabel = {
  provider: SocialProvider;
  label: string;
  icon: IconType;
};

const providerLabels: ProviderLabel[] = [
  { label: "", icon: SiGoogle, provider: "google" },
  // { label: "Continue with Zoom", icon: SiZoom, provider: "zoom" },
];

export function SocialSignInButton({
  providerLabel: { provider, label, icon },
  disabled = false,
}: SocialSignInButtonProps) {
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations("SocialSignIn");

  const redirectUrl = getRedirectUrlFromSearch(searchParams, "/creator");

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await signIn.social({
        provider,
        callbackURL: redirectUrl,
      });
      // Note: signIn.social will redirect automatically, so setIsLoading(false) won't be reached
    } catch {
      // Error handling is done by Better Auth - user will be redirected on error
      setIsLoading(false);
    }
  };

  const Icon = icon;

  return (
    <Button
      type="button"
      outline
      className="w-full"
      disabled={disabled || isLoading}
      onClick={handleClick}
    >
      <Icon data-slot="icon" />

      {isLoading
        ? t("loading")
        : label || (provider === "google" ? t("continueWithGoogle") : label)}
    </Button>
  );
}

type SocialSignInProps = {
  disabled?: boolean;
};

export function SocialSignIn({ disabled = false }: SocialSignInProps) {
  const t = useTranslations("SocialSignIn");

  return (
    <Suspense fallback={<div>{t("loading")}</div>}>
      <div className="grid w-full grid-cols-1 gap-4">
        {providerLabels.map((providerLabel) => (
          <SocialSignInButton
            key={providerLabel.provider}
            providerLabel={providerLabel}
            disabled={disabled}
          />
        ))}
      </div>
    </Suspense>
  );
}
